/**
 * VenturaZ — timezone-aware date/time helpers.
 *
 * Rules:
 *   - Detect the user's timezone via `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 *   - Fall back to `Asia/Kolkata` (IST) if the browser can't resolve one.
 *   - Users can override their preferred timezone via localStorage key
 *     `venturaz.tz` (settable from a future profile screen).
 *
 * All date/time surfaces in the app should route through these helpers so
 * a single source-of-truth controls formatting.
 */

const DEFAULT_TZ = "Asia/Kolkata";
const LS_KEY = "venturaz.tz";

let _cached = null;

/** Returns the effective timezone (user override → browser → IST). */
export function getUserTimezone() {
  if (_cached) return _cached;
  try {
    const override = typeof window !== "undefined" ? window.localStorage?.getItem(LS_KEY) : null;
    if (override) {
      _cached = override;
      return override;
    }
  } catch { /* ignore storage errors */ }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    _cached = tz || DEFAULT_TZ;
    return _cached;
  } catch {
    _cached = DEFAULT_TZ;
    return DEFAULT_TZ;
  }
}

/** Persist a user's timezone override. Pass `null` to clear. */
export function setUserTimezone(tz) {
  try {
    if (!tz) window.localStorage.removeItem(LS_KEY);
    else window.localStorage.setItem(LS_KEY, tz);
  } catch { /* ignore */ }
  _cached = tz || null;
}

function _parse(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Format as `dd MMM yyyy` in the user's timezone (e.g., `25 Jul 2026`). */
export function formatDate(value, opts = {}) {
  const d = _parse(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: opts.tz || getUserTimezone(),
  }).format(d);
}

/** Format as `dd MMM yyyy, hh:mm AM/PM` in the user's timezone. */
export function formatDateTime(value, opts = {}) {
  const d = _parse(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: opts.tz || getUserTimezone(),
  }).format(d);
}

/** Format as `hh:mm AM/PM` only. */
export function formatTime(value, opts = {}) {
  const d = _parse(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: opts.tz || getUserTimezone(),
  }).format(d);
}

/** Human-readable relative label ("in 2 hours", "3 days ago", "just now"). */
export function formatRelative(value) {
  const d = _parse(value);
  if (!d) return "—";
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const table = [
    [60 * 1000, 1000, "second"],
    [60 * 60 * 1000, 60 * 1000, "minute"],
    [24 * 60 * 60 * 1000, 60 * 60 * 1000, "hour"],
    [7 * 24 * 60 * 60 * 1000, 24 * 60 * 60 * 1000, "day"],
    [30 * 24 * 60 * 60 * 1000, 7 * 24 * 60 * 60 * 1000, "week"],
    [365 * 24 * 60 * 60 * 1000, 30 * 24 * 60 * 60 * 1000, "month"],
    [Infinity, 365 * 24 * 60 * 60 * 1000, "year"],
  ];
  for (const [limit, div, unit] of table) {
    if (abs < limit) return rtf.format(Math.round(diffMs / div), unit);
  }
  return formatDate(value);
}

/** Suffix used when displaying alongside the timezone (e.g., "(IST)"). */
export function timezoneSuffix() {
  const tz = getUserTimezone();
  // Map common IANA zones to friendly abbreviations that browsers render as
  // numeric offsets (e.g. Chromium returns "GMT+5:30" for Asia/Kolkata).
  const OVERRIDES = {
    "Asia/Kolkata": "IST",
    "Asia/Calcutta": "IST",
    "Asia/Colombo": "IST",
  };
  if (OVERRIDES[tz]) return OVERRIDES[tz];
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const value = parts.find((p) => p.type === "timeZoneName")?.value;
    // If the browser gives back a numeric offset (e.g. "GMT+5:30"), fall back
    // to the city portion of the IANA name for a friendlier label.
    if (!value || /^GMT[+\-]/i.test(value)) {
      return tz.split("/").pop().replace(/_/g, " ");
    }
    return value;
  } catch {
    return tz;
  }
}
